// src/features/dashboard/hooks/useDashboard.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@vanialabs/core-auth';
import { resolveAccessToken } from '../../../app/auth/authToken';
import { dashboardCache, scopedCacheKey } from '../cache/dashboardCache';
import { fetchDashboardSummary } from '../api/dashboard.api';
import { mapDashboardSummary } from '../mappers/dashboard.mapper';
import type { DashboardData } from '../mappers/dashboard.mapper';

export type { DashboardData };

const CACHE_NAMESPACE = 'dashboard-data';

/**
 * Cache-first dashboard data, backed by the real
 * `GET /api/dashboard/summary` endpoint via `dashboard.api.ts` +
 * `dashboard.mapper.ts`. Mirrors `useDashboardSpotlight`'s pattern:
 *
 *   First visit (no cache):  skeleton  -> fetch -> store snapshot
 *   Later visits (cached):   render immediately -> refresh silently -> update
 *
 * `isInitialLoading` is only ever true when there is nothing cached
 * yet. `isRefreshing` is true while a background refresh is in
 * flight behind data that's already on screen -- the UI should not
 * react to it. This replaces `useDashboardData` (mock-backed); the
 * `DashboardData` contract is unchanged so no consuming component
 * needed to change.
 */
export function useDashboard() {
  const isAuthInitialized = useAuthStore((state) => state.isInitialized);
  const session = useAuthStore((state) => state.session);
  const accessToken = resolveAccessToken(session);

  const cacheKey = scopedCacheKey(CACHE_NAMESPACE, accessToken);

  const [data, setData] = useState<DashboardData | null>(
    () => dashboardCache.get<DashboardData>(cacheKey) ?? null
  );
  const [isInitialLoading, setIsInitialLoading] = useState(
    () => dashboardCache.get<DashboardData>(cacheKey) === null
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(() => {
    if (!isAuthInitialized) {
      return;
    }

    const requestId = ++requestIdRef.current;
    const cached = dashboardCache.get<DashboardData>(cacheKey);
    const hasCached = cached !== null;

    if (hasCached) {
      // Cache-first: show the cached snapshot immediately, refresh silently.
      setData(cached);
      setIsInitialLoading(false);
      setIsRefreshing(true);
    } else {
      setIsInitialLoading(true);
    }
    setError(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    fetchDashboardSummary({ signal: controller.signal })
      .then((dto) => {
        if (requestIdRef.current !== requestId) return;

        const fresh = mapDashboardSummary(dto);
        dashboardCache.set(cacheKey, fresh);
        setData(fresh);
        setIsInitialLoading(false);
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return;

        // A background refresh failure should never blank out a dashboard
        // the user is already looking at -- only surface the error state
        // when there was nothing cached to fall back to.
        if (!hasCached) {
          setData(null);
          setError(
            err instanceof Error ? err : new Error('Failed to load dashboard data')
          );
        }
        setIsInitialLoading(false);
        setIsRefreshing(false);
      });
  }, [isAuthInitialized, cacheKey]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const retry = useCallback(() => {
    load();
  }, [load]);

  return { data, isInitialLoading, isRefreshing, error, retry };
}
